import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import * as DependenciesV2Store from '../../../../redux/dependenciesV2';
import List from '../../../generic/List';
import useFetchDependencyAction from '../hooks/useFetchDependencyAction';
import type { Dependency } from '../../../../../shared/dependenciesV2';
import type { DependencyV2Modals } from '../hooks/useDependencyModals';

import DependencyTableRow from './DependencyTableRow';
import * as FallbackScreens from './FallbackScreens';

/**
 * Generate a unique dependency key so that React can properly disambiguate
 * each row. Since unknown deps in release groups appear separately, the dep's
 * locator isn't enough to guarantee uniqueness. Append the dep's root projects
 * in order to avoid collisions.
 */
function dependencyKey(dependency: Dependency): string {
  const rootLocators = dependency.rootProjects.map((rootProject) => rootProject.revision);
  const fields = [dependency.locator, ...rootLocators];
  return fields.join(',');
}

type Props = {
  modals: DependencyV2Modals;
  setSelectedDependencyRevisionLocator: (locator: string) => void;
};

const DependencyTableContents: VoidFunctionComponent<Props> = ({
  modals,
  setSelectedDependencyRevisionLocator,
}) => {
  const isLoading = useSelector(DependenciesV2Store.selectors.getAreDependenciesLoading);
  const errors = useSelector(DependenciesV2Store.selectors.getFetchDependenciesErrors);
  const rowCount = useSelector(DependenciesV2Store.selectors.getCount);
  const dependencies = useSelector(DependenciesV2Store.selectors.getDependencies);
  const totalDependencyCount = useSelector(DependenciesV2Store.selectors.getTotalDependencies);
  const areFiltersDefault = useSelector(DependenciesV2Store.selectors.getAreFiltersDefault);

  const resetFilters = useFetchDependencyAction(DependenciesV2Store.actions.resetFilters);

  if (isLoading) {
    return (
      <>
        {Array.from({ length: rowCount }).map(() => (
          <List.RowLoading />
        ))}
      </>
    );
  }

  if (errors) {
    return <FallbackScreens.Error />;
  }

  if (totalDependencyCount === 0 && areFiltersDefault) {
    return <FallbackScreens.NoDependencies />;
  }

  if (dependencies && dependencies.length > 0) {
    return (
      <>
        {dependencies.map((dependency: Dependency) => (
          <DependencyTableRow
            key={dependencyKey(dependency)}
            dependency={dependency}
            modals={modals}
            setSelectedDependencyRevisionLocator={setSelectedDependencyRevisionLocator}
          />
        ))}
      </>
    );
  }

  return <FallbackScreens.NoResults onResetButtonClick={resetFilters} />;
};

export default DependencyTableContents;
